<template>
  <div>
    <div
      class="mb-2 flex items-center justify-between tablet:flex-col tablet:items-start tablet:justify-normal laptop:flex-row laptop:items-center laptop:justify-between"
    >
      <h4 class="mb-1 text-headline-s laptop:mb-0">{{ title }}</h4>

      <button class="btn-link-neutral-s" @click="resetFilters">
        {{ $t('job_list.sidebar.reset') }}
      </button>
    </div>

    <div class="space-y-3">
      <JobListFacetsItem
        v-for="facet in defaultVisibleFacets"
        :key="facet.facetId"
        :facet="facet"
        :facet-type="facetType"
        :checked="isFacetFilterSelected(facet.facetId)"
        :translated="translated"
        @change="onFacetFilterChange"
      />

      <template v-if="expandableFacets.length">
        <div v-show="isExpanded" :id="`${facetType}-more`" class="space-y-3 border-t border-t-grey-300 pt-3">
          <JobListFacetsItem
            v-for="facet in expandableFacets"
            :key="facet.facetId"
            :facet="facet"
            :facet-type="facetType"
            :checked="isFacetFilterSelected(facet.facetId)"
            :translated="translated"
            @change="onFacetFilterChange"
          />
        </div>

        <button class="btn-link-neutral-s flex w-full items-center justify-between" @click="isExpanded = !isExpanded">
          {{ moreFacetsText }}

          <img
            src="~/assets/images/arrow-down-black.svg"
            class="w-3.5 transition-transform duration-150"
            :class="{ 'rotate-180': isExpanded }"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import JobListFacetsItem from '@/components/JobListFacetsItem.vue'

import { useNuxtApp } from '#imports'
import type { SearchFacetFragment } from '~/gql/jobs/types.generated'

interface JobListFacetsProps {
  title: string
  facetType: string
  facets: SearchFacetFragment[]
  translated: boolean
}

const DEFAULT_VISIBLE_FACET_FILTERS_COUNT = 5

const props = defineProps<JobListFacetsProps>()

const route = useRoute()
const router = useRouter()
const { $i18n } = useNuxtApp()

const isExpanded = ref(false)
const selectedFilters = ref([route.query[props.facetType] || []].flat())

const defaultVisibleFacets = computed(() => props.facets.slice(0, DEFAULT_VISIBLE_FACET_FILTERS_COUNT))

const expandableFacets = computed(() => props.facets.slice(DEFAULT_VISIBLE_FACET_FILTERS_COUNT))

const moreFacetsText = computed(() => {
  if (isExpanded.value) {
    return $i18n.t('job_list.sidebar.show_less')
  }

  return $i18n.t('job_list.sidebar.show_more', {
    num: expandableFacets.value.length,
  })
})

watch(selectedFilters, (newFilters) => {
  router.push({
    params: {
      page: '1',
    },

    query: {
      ...route.query,
      [props.facetType]: newFilters,
    },
  })
})

const isFacetFilterSelected = (facetId: string) => selectedFilters.value.includes(facetId)

const onFacetFilterChange = ({ facetId, isChecked }: { facetId: string; isChecked: boolean }) => {
  if (isChecked) {
    selectedFilters.value = [...selectedFilters.value, facetId]
  } else {
    selectedFilters.value = selectedFilters.value.filter((selectedFacet) => selectedFacet !== facetId)
  }
}

const resetFilters = () => {
  selectedFilters.value = []
}
</script>
